/**
 * The White Whale — client-side PRESENTATION (ZERO wire, ZERO sim).
 *
 * The server owns the whale completely: it migrates it, flips it between SURFACE and DIVE, and broadcasts a
 * marker `{x, y, heading, surfaced}` map-wide at ~5 Hz (`WHALE.MARK_EVERY_TICKS`). Everything in this file is
 * what the CLIENT does with those five samples per second to make a 40-tonne animal read like an animal
 * instead of a prop being teleported around.
 *
 * Two laws hold the whole thing together:
 *
 *  1. **The vertical is a DEPTH below the local water surface — never an absolute world Y.** The sea moves;
 *     what we animate is the whale's *relation* to it. Every frame the renderer draws it at
 *     `y = oceanHeight(x, z) - depth`, so a surfaced whale rides the swell exactly (calm or storm, no phase
 *     lag) and can never hover. The v1 bug was precisely this: it drew the model — whose origin is its BELLY —
 *     at `oceanHeight + 1.2`, i.e. the entire 12.7 m body floating a metre above the sea.
 *  2. **Travel is DEAD-RECKONED, not eased.** The server moves the whale at a constant `WHALE.SPEED` along its
 *     heading, so the client integrates exactly that model and only applies a gentle corrective pull toward the
 *     latest marker. Easing straight at a 5 Hz marker makes the animal surge-and-stall five times a second;
 *     dead-reckoning + a soft correction is smooth AND never rubber-bands (the correction is centimetres).
 *
 * The breach/sound choreography is a spring, not a keyframe track: `depth` is a damped mass pulled toward the
 * phase's target, kicked with an impulse when the animal commits. That is what makes the emersion read as a
 * body bursting through the surface and settling, rather than an elevator.
 */
import { WHALE } from '@piratas/protocol';

/** The wire marker as the client keeps it (net.ts `whale`), trimmed to what the presentation needs. */
export interface WhaleMarker { x: number; y: number; heading: number; surfaced: boolean }

/* ------------------------------------------------------------------------------------------------------ *
 *  PREVIEW (`?whale=1` / `?whale=die`) — a synthetic marker so the LOOK can be audited on demand.
 *  The real whale is scheduled (first ~12 min after boot, then 1–2×/day), which makes iterating on its art
 *  impossible. This reproduces the server's marker EXACTLY in shape and cadence (quantized to ~5 Hz, constant
 *  cruise at WHALE.SPEED, phase flips on WHALE-like timers) so anything that looks smooth here looks smooth
 *  in the real hunt. Shorter phase timers only — never a different motion model.
 * ------------------------------------------------------------------------------------------------------ */
export const WHALE_PREVIEW = {
  RADIUS: 62,      // the preview whale circles the captain at this radius (stays in frame, close enough to read)
  SURFACE_S: 11,   // shortened WHALE.SURFACE_S (20) so a full cycle fits in a screenshot pass
  DIVE_S: 7,       // shortened WHALE.DIVE_S (8)
  MARK_HZ: 30 / WHALE.MARK_EVERY_TICKS, // the server's real marker cadence (~5 Hz) — quantize to it
  DIE_AT_S: 24,    // `?whale=die`: fell it this long in (mid second surfacing) to review the death beat
} as const;

/**
 * The synthetic marker at `elapsed` seconds, circling (ox, oz) — quantized to the wire's ~5 Hz cadence so the
 * client sees the same stepped input it gets in production. Returns null once a `?whale=die` run has felled it.
 */
export function previewMarkerAt(elapsed: number, ox: number, oz: number, die = false): WhaleMarker | null {
  if (die && elapsed >= WHALE_PREVIEW.DIE_AT_S) return null;
  const q = Math.floor(Math.max(0, elapsed) * WHALE_PREVIEW.MARK_HZ) / WHALE_PREVIEW.MARK_HZ; // wire cadence
  const R = WHALE_PREVIEW.RADIUS;
  const ang = (q * WHALE.SPEED) / R;                       // constant cruise along the circle
  const cycle = WHALE_PREVIEW.SURFACE_S + WHALE_PREVIEW.DIVE_S;
  const inCycle = q % cycle;
  return {
    x: ox + Math.cos(ang) * R,
    y: oz + Math.sin(ang) * R,
    heading: ang + Math.PI / 2,                            // tangent to the circle
    surfaced: inCycle < WHALE_PREVIEW.SURFACE_S,
  };
}

/* ------------------------------------------------------------------------------------------------------ *
 *  ART TUNING — every knob the whale's BEHAVIOUR hangs on. Render-only, so it lives here beside the code
 *  that uses it (the shared/gameplay whale constants stay in @piratas/protocol WHALE: hp, speed, phases).
 * ------------------------------------------------------------------------------------------------------ */
export const WHALE_ART = {
  /** Resting DRAFT while it swims at the surface, as a fraction of body LENGTH. Length, not height: the model
   *  is an orca whose bounding box is half DORSAL FIN, so height is a lie about how deep the animal floats.
   *  0.20 × 26 m ≈ 5.2 m of draft — back and fin out, belly and flippers in the water, which is what a cetacean
   *  at the surface actually looks like (~80 % of the body wet). */
  RIDE_L: 0.20,
  /** Depth of the belly while sounding, as a fraction of length. Deep enough that it is gone, shallow enough
   *  that the murk still hints at something passing under the hull. */
  DEEP_L: 0.46,
  /** The belly may never come closer to the surface than this × length, however hard the breach throws it.
   *  This is the hard "it can never fly" clamp — the thing v1 had no notion of. */
  MIN_RIDE_L: 0.072,
  /** Within this × length of its resting draft the animal counts as "up" (wake + foam fade in over it). */
  SURFACING_L: 0.13,

  RISE_K: 7, RISE_C: 3.6,    // breach spring: underdamped → it bursts through and settles in ~1.5 s
  RISE_V0: 4.2,              // m/s upward impulse at the moment it commits to the surface
  SINK_K: 2.2, SINK_C: 3.4,  // sounding spring: soft + overdamped → a heavy, unhurried slide under
  DIE_K: 1.0, DIE_C: 2.8,    // the carcass: slower still

  ARCH_S: 1.5,               // seconds of arch (fluke-up) before the sounding whale commits to the depth
  ARCH_RAMP: 0.55,           // fraction of ARCH_S spent reaching full arch — the rest HOLDS the flukes up
  ARCH_LIFT: 0.7,            // m the back humps up during the arch (the classic rounding-out)
  ARCH_PITCH: -0.85,         // rad (~−49°) nose-down at the top of the arch — this is what throws the flukes clear
  RISE_PITCH: 0.38,          // rad (~+22°) nose-up on the way up
  PITCH_K: 20, PITCH_C: 7.6, // pitch spring
  WAVE_PITCH: 0.75,          // how much of the local wave slope the surfaced body takes (1 = rides it exactly)
  WAVE_ROLL: 0.55,
  /** Height of the roll/pitch pivot above the model's origin (its belly), as a fraction of body HEIGHT.
   *  Without it a belly-up carcass would rotate about its own belly and drop the whole animal under the sea —
   *  rotating about the body's core keeps it straddling the waterline whatever the roll. */
  ROLL_PIVOT: 0.22,

  BLOW_MIN_S: 5.5, BLOW_MAX_S: 9.5, // seconds between blows while surfaced (a real rorqual blows in series)
  YAW_EASE: 3.2,             // rad/s-ish exponential rate the drawn heading chases the marker's
  TURN_MAX: 1.1,             // rad/s hard cap on the drawn turn (≈2.7× the server's WHALE.TURN_RATE) — no snap
  POS_EASE: 2.4,             // exponential rate the dead-reckoned position is pulled onto the marker

  DEATH_S: 11,               // the death beat: roll belly-up and float, then sink away
  DEATH_FLOAT_S: 3.4,        // seconds the fresh carcass wallows at the surface before it goes down
  LEAVE_S: 4.5,              // GONE / out of sight: one last sounding, then dispose
} as const;

/** What the body is doing right now. */
export type WhalePhase = 'deep' | 'rising' | 'up' | 'sounding' | 'dying' | 'leaving' | 'done';

/** One-shot effect the body asks the renderer to draw. Positions are WORLD (x, z); `y` is the water height. */
export interface WhaleFx {
  kind: 'breach' | 'settle' | 'fluke' | 'slip' | 'blow' | 'death' | 'bubble';
  x: number; z: number; power: number;
}

const TAU = Math.PI * 2;
const norm = (a: number): number => { const r = (a + Math.PI) % TAU; return (r < 0 ? r + TAU : r) - Math.PI; };
const smooth = (k: number): number => { const c = Math.max(0, Math.min(1, k)); return c * c * (3 - 2 * c); };

/**
 * The whale's rendered BODY: takes the 5 Hz marker in, produces a smooth, physically-plausible pose out.
 * Pure math + a small FX queue — no three.js, so it is unit-testable against a synthetic ocean.
 */
export class WhaleBody {
  x = 0; z = 0; yaw = 0;
  /** Metres the model's ORIGIN (its belly) sits below the local water surface. The whole animation is here. */
  depth = 12;
  pitch = 0; roll = 0;
  phase: WhalePhase = 'deep';
  /** Effects to draw this frame; the renderer drains it. */
  readonly fx: WhaleFx[] = [];
  /** 0..1 how much of the body is out of the water — the renderer fades the wake/foam with it. */
  surfacing = 0;

  private vDepth = 0;
  private vPitch = 0;
  private phaseT = 0;
  private blowIn = 3;
  private marker: WhaleMarker | null = null;
  private started = false;
  private breached = false;   // the breach burst has fired for this rise
  private fluked = false;     // the fluke-up burst has fired for this sounding
  private slipped = false;    // the "body slips under" ring has fired for this sounding
  private bubbleAcc = 0;
  private deathDrift = 0;
  /** The last DISTINCT marker + how long ago it landed, so the chase target can be extrapolated forward at
   *  the server's own cruise speed instead of stepping 5×/s (see the travel block in `step`). */
  private markX = 0; private markZ = 0; private markHeading = 0; private markAge = 0;

  constructor(private bodyH = 12.7, private bodyL = 26) {}

  /** Re-measure once the real GLB lands (the procedural stand-in has different proportions). */
  setBody(h: number, l: number): void { this.bodyH = h; this.bodyL = l; }

  /** Where the belly rests when the animal is simply swimming at the surface. */
  get restDepth(): number { return this.bodyL * WHALE_ART.RIDE_L; }
  /** Depth of the belly while sounding. */
  private get deepDepth(): number { return this.bodyL * WHALE_ART.DEEP_L; }

  /** Feed the latest server marker (or the preview's). Snaps hard only on the very first one. */
  sync(m: WhaleMarker): void {
    // a marker only "lands" when the server actually moved it — the same one re-fed every frame must not
    // reset the extrapolation clock, or the target would freeze between wire updates (the sawtooth again).
    if (m.x !== this.markX || m.y !== this.markZ) {
      this.markX = m.x; this.markZ = m.y; this.markHeading = m.heading; this.markAge = 0;
    }
    this.marker = m;
    if (!this.started) {
      this.started = true;
      this.x = m.x; this.z = m.y; this.yaw = m.heading;
      this.markX = m.x; this.markZ = m.y; this.markHeading = m.heading; this.markAge = 0;
      // born below: even a whale that spawns SURFACED rises into view instead of popping in at full size —
      // this is the v1 "pop-in no spawn" (the mesh was placed at its target the frame it was created).
      this.phase = 'deep'; this.depth = this.deepDepth; this.vDepth = 0;
      this.pitch = 0; this.roll = 0;
    }
  }

  /** EV_WHALE state DEAD — the hunt is over; play the carcass beat, then `done`. */
  kill(): void {
    if (this.phase === 'dying' || this.phase === 'done') return;
    this.phase = 'dying'; this.phaseT = 0; this.vDepth = Math.min(this.vDepth, 0);
    this.deathDrift = WHALE.SPEED * 0.55;
    this.fx.push({ kind: 'death', x: this.x, z: this.z, power: 1 });
  }

  /** The marker is gone (EV_WHALE GONE, or it left AOI): one last sounding, then `done`. */
  leave(): void {
    if (this.phase === 'dying' || this.phase === 'done' || this.phase === 'leaving') return;
    this.phase = 'leaving'; this.phaseT = 0; this.fluked = false; this.slipped = false;
  }

  /** True once the beat is finished and the renderer may dispose the mesh. */
  get finished(): boolean { return this.phase === 'done'; }

  /**
   * Advance one frame. `waterAt(x, z)` must be the SAME ocean height function the sea mesh renders with
   * (amplitude included) — that is what glues the animal to the swell in calm water and in a gale alike.
   */
  step(dt: number, waterAt: (x: number, z: number) => number): void {
    if (this.phase === 'done') return;
    const d = Math.min(0.05, Math.max(0, dt)); // a tab-switch stall must not teleport the animal
    this.phaseT += d;
    const A = WHALE_ART;

    /* ---- phase machine: driven by the marker's surfaced flag (edges), never by a timer ---- */
    const m = this.marker;
    const alive = this.phase !== 'dying' && this.phase !== 'leaving';
    if (alive && m) {
      if (m.surfaced && (this.phase === 'deep' || this.phase === 'sounding')) {
        this.phase = 'rising'; this.phaseT = 0; this.breached = false;
        this.vDepth -= A.RISE_V0;              // it COMMITS: a real upward impulse, not a lerp
      } else if (!m.surfaced && (this.phase === 'up' || this.phase === 'rising')) {
        this.phase = 'sounding'; this.phaseT = 0; this.fluked = false; this.slipped = false;
      }
      // the rise is over once the body has settled back onto its waterline
      if (this.phase === 'rising' && (this.phaseT > 3.2 || (this.depth > this.restDepth - 0.25 && Math.abs(this.vDepth) < 0.7))) {
        this.phase = 'up'; this.phaseT = 0;
        this.blowIn = Math.min(this.blowIn, 0.12); // a whale BLOWS the moment it surfaces — that is the tell
      }
      if (this.phase === 'sounding' && this.phaseT > A.ARCH_S + 3) { this.phase = 'deep'; this.phaseT = 0; }
    }

    /* ---- travel: dead-reckon the server's own motion model, then pull softly onto the EXTRAPOLATED marker.
     *  Chasing the raw marker would be smooth for 200 ms and then take a 1.3 m step every time a new one lands
     *  — a 5 Hz sawtooth in the drawn speed (the animal surging and stalling). Because the server's motion model
     *  is known exactly (constant WHALE.SPEED along the heading), the client can advance the LAST marker itself
     *  and chase a target that moves continuously: the correction becomes a smoothly decaying offset. ---- */
    let speed: number = WHALE.SPEED;
    if (this.phase === 'dying') { this.deathDrift *= Math.pow(0.22, d); speed = this.deathDrift; }
    this.markAge += d;
    if (m && alive) {
      // turn toward the marker's heading — eased, and hard-limited to a plausible turn rate so even a marker
      // that jumps half the compass (re-entering AOI, a fresh spawn) is swung to, never snapped to.
      const want = norm(m.heading - this.yaw) * (1 - Math.pow(0.02, d * A.YAW_EASE / 3.2));
      const cap = A.TURN_MAX * d;
      this.yaw = norm(this.yaw + Math.max(-cap, Math.min(cap, want)));
    }
    this.x += Math.cos(this.yaw) * speed * d;
    this.z += Math.sin(this.yaw) * speed * d;
    if (m && alive) {
      const tx = this.markX + Math.cos(this.markHeading) * WHALE.SPEED * this.markAge;
      const tz = this.markZ + Math.sin(this.markHeading) * WHALE.SPEED * this.markAge;
      const k = 1 - Math.exp(-A.POS_EASE * d);
      this.x += (tx - this.x) * k;
      this.z += (tz - this.z) * k;
    }

    /* ---- vertical: one damped spring on DEPTH, retuned per phase ---- */
    const deep = this.deepDepth;
    let target: number = deep, K: number = A.SINK_K, C: number = A.SINK_C;
    switch (this.phase) {
      case 'rising': target = this.restDepth; K = A.RISE_K; C = A.RISE_C; break;
      case 'up':     target = this.restDepth; K = A.RISE_K * 0.55; C = A.RISE_C * 1.35; break;
      case 'sounding':
        // the arch: the back HUMPS up first (rounding out), and only then does it commit downward
        target = this.phaseT < A.ARCH_S ? this.restDepth - A.ARCH_LIFT * smooth(this.phaseT / A.ARCH_S) : deep;
        K = this.phaseT < A.ARCH_S ? A.RISE_K * 0.5 : A.SINK_K; C = this.phaseT < A.ARCH_S ? A.RISE_C : A.SINK_C;
        break;
      case 'leaving': target = deep * 1.8; break;
      case 'dying':
        // A fresh carcass WALLOWS belly-up before it goes down — the beat is the pause, not the sinking.
        // Belly-up the body hangs BELOW the roll pivot, so the float target is measured from it: the belly
        // plane ends up ~0.1 L clear of the water, i.e. a broad pale expanse the whole sea can read.
        target = this.phaseT < A.DEATH_FLOAT_S
          ? this.bodyH * (2 * A.ROLL_PIVOT) - this.bodyL * 0.1
          : deep * 0.95;
        K = this.phaseT < A.DEATH_FLOAT_S ? A.SINK_K : A.DIE_K; C = A.DIE_C;
        break;
      default: break;
    }
    this.vDepth += ((target - this.depth) * K - this.vDepth * C) * d;
    this.depth += this.vDepth * d;
    const floor = this.bodyL * A.MIN_RIDE_L;     // the hard "it can never fly" clamp
    if (this.depth < floor) { this.depth = floor; if (this.vDepth < 0) this.vDepth *= -0.25; }
    this.surfacing = Math.max(0, Math.min(1, 1 - (this.depth - this.restDepth) / (this.bodyL * A.SURFACING_L)));

    /* ---- pitch: a spring too, so nothing ever snaps ---- */
    const half = this.bodyL * 0.42;
    const c = Math.cos(this.yaw), s = Math.sin(this.yaw);
    let pTarget = 0;
    const wavePitch = (): number => {
      const hn = waterAt(this.x + c * half, this.z + s * half);
      const ht = waterAt(this.x - c * half, this.z - s * half);
      return Math.atan2(hn - ht, half * 2) * A.WAVE_PITCH;
    };
    switch (this.phase) {
      case 'rising':  pTarget = this.depth > this.restDepth + 0.7 ? A.RISE_PITCH : wavePitch() - 0.05; break;
      case 'up':      pTarget = wavePitch(); break;
      case 'sounding':
        pTarget = this.phaseT < A.ARCH_S
          ? A.ARCH_PITCH * smooth(this.phaseT / (A.ARCH_S * A.ARCH_RAMP))   // ramps, then HOLDS flukes-up
          : A.ARCH_PITCH * 0.4 * Math.max(0, 1 - (this.phaseT - A.ARCH_S) / 2.2);
        break;
      case 'leaving': pTarget = A.ARCH_PITCH * 0.7 * Math.max(0, 1 - this.phaseT / 2.4); break;
      case 'dying':   pTarget = 0.08; break;
      default:        pTarget = Math.sin(this.phaseT * 0.5) * 0.03; break; // idle undulation at depth
    }
    this.vPitch += ((pTarget - this.pitch) * A.PITCH_K - this.vPitch * A.PITCH_C) * d;
    this.pitch += this.vPitch * d;

    /* ---- roll: the sea rolls it while up; the carcass rolls belly-up ---- */
    let rTarget = Math.sin(this.phaseT * 0.55 + this.x * 0.01) * 0.05;
    if (this.phase === 'up' || this.phase === 'rising') {
      const px = -s * this.bodyL * 0.16, pz = c * this.bodyL * 0.16;
      const hp = waterAt(this.x + px, this.z + pz), hs = waterAt(this.x - px, this.z - pz);
      rTarget += Math.atan2(hs - hp, this.bodyL * 0.32) * A.WAVE_ROLL * this.surfacing;
    }
    if (this.phase === 'dying') rTarget = Math.PI * smooth(this.phaseT / 2.6);
    this.roll += (rTarget - this.roll) * (1 - Math.pow(0.02, d * 2));

    /* ---- the beats: one-shot effects, each latched so it fires exactly once per cycle ---- */
    if (this.phase === 'rising' && !this.breached && this.depth < this.restDepth + this.bodyL * 0.06) {
      this.breached = true;
      this.fx.push({ kind: 'breach', x: this.x + c * half * 0.7, z: this.z + s * half * 0.7, power: Math.min(1.6, Math.abs(this.vDepth) / 5) });
    }
    if (this.phase === 'up' && this.phaseT < d * 1.5) this.fx.push({ kind: 'settle', x: this.x, z: this.z, power: 1 });
    if (this.phase === 'sounding' || this.phase === 'leaving') {
      if (!this.fluked && this.pitch < A.ARCH_PITCH * 0.62) {
        this.fluked = true;
        this.fx.push({ kind: 'fluke', x: this.x - c * half, z: this.z - s * half, power: 1 });
      }
      if (!this.slipped && this.depth > this.restDepth + this.bodyL * 0.075) {
        this.slipped = true;
        this.fx.push({ kind: 'slip', x: this.x, z: this.z, power: 1 });
      }
    }
    if (this.phase === 'up') {
      this.blowIn -= d;
      if (this.blowIn <= 0) {
        this.blowIn = A.BLOW_MIN_S + Math.random() * (A.BLOW_MAX_S - A.BLOW_MIN_S);
        this.fx.push({ kind: 'blow', x: this.x + c * half * 0.78, z: this.z + s * half * 0.78, power: 1 });
      }
    }
    if (this.phase === 'dying') {
      this.bubbleAcc += d;
      if (this.bubbleAcc > 0.22) { this.bubbleAcc = 0; this.fx.push({ kind: 'bubble', x: this.x, z: this.z, power: 1 - this.phaseT / A.DEATH_S }); }
      if (this.phaseT > A.DEATH_S) this.phase = 'done';
    }
    if (this.phase === 'leaving' && this.phaseT > A.LEAVE_S) this.phase = 'done';
  }
}
